import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { Lock, LogOut, RefreshCw, Download, Inbox, Activity, Search, X, FileDown } from "lucide-react";
import { Logo } from "@/components/site/Logo";
import { IMPROVEMENT_OPTIONS } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "stockaris-admin-token";

const scoreColor = (v) => (v >= 75 ? "text-volt" : v >= 50 ? "text-white" : "text-destructive");

const RANGES = [
  { key: "all", label: "All time", days: null },
  { key: "today", label: "Today", days: 0 },
  { key: "7d", label: "7 days", days: 7 },
  { key: "30d", label: "30 days", days: 30 },
];

const LEAD_STATUSES = ["New", "Contacted", "Won"];
const statusStyle = {
  New: "border-slate-300 bg-slate-100 text-slate-600",
  Contacted: "border-volt/40 bg-volt/10 text-volt",
  Won: "border-emerald-300 bg-emerald-50 text-emerald-600",
};

const withinRange = (iso, days) => {
  if (days === null || days === undefined) return true;
  if (!iso) return false;
  const d = new Date(iso);
  const now = new Date();
  if (days === 0) return d.toDateString() === now.toDateString();
  const cutoff = new Date(now.getTime() - days * 86400000);
  return d >= cutoff;
};

const toCSV = (rows, columns) => {
  const esc = (v) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = columns.map((c) => esc(c.label)).join(",");
  const body = rows.map((r) => columns.map((c) => esc(c.get(r))).join(",")).join("\n");
  return `${header}\n${body}`;
};

const downloadCSV = (csv, filename) => {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [draft, setDraft] = useState("");
  const [tab, setTab] = useState("demos");
  const [demos, setDemos] = useState([]);
  const [checks, setChecks] = useState([]);
  const [stats, setStats] = useState({ demo_requests: 0, health_checks: 0 });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [area, setArea] = useState("all");
  const [range, setRange] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  const rangeDays = RANGES.find((r) => r.key === range)?.days;

  const filteredDemos = useMemo(() => {
    const q = query.trim().toLowerCase();
    return demos.filter((d) => {
      if (!withinRange(d.created_at, rangeDays)) return false;
      if (area !== "all" && d.improvement_area !== area) return false;
      if (statusFilter !== "all" && (d.status || "New") !== statusFilter) return false;
      if (!q) return true;
      return [d.name, d.company, d.email, d.current_wms].some((v) => (v || "").toLowerCase().includes(q));
    });
  }, [demos, query, area, statusFilter, rangeDays]);

  const filteredChecks = useMemo(() => {
    const q = query.trim().toLowerCase();
    return checks.filter((c) => {
      if (!withinRange(c.created_at, rangeDays)) return false;
      if (!q) return true;
      return [c.email, c.company].some((v) => (v || "").toLowerCase().includes(q));
    });
  }, [checks, query, rangeDays]);

  const clearFilters = () => { setQuery(""); setArea("all"); setRange("all"); setStatusFilter("all"); };
  const hasFilters = query || area !== "all" || range !== "all" || statusFilter !== "all";

  const updateStatus = async (id, status) => {
    const prev = demos;
    setDemos((ds) => ds.map((d) => (d.id === id ? { ...d, status } : d)));
    try {
      await axios.patch(`${API}/admin/demo-requests/${id}/status`, { status }, { headers: { "X-Admin-Token": token } });
    } catch {
      setDemos(prev);
    }
  };

  const exportCurrent = () => {
    const date = new Date().toISOString().slice(0, 10);
    if (tab === "demos") {
      const cols = [
        { label: "Date", get: (r) => (r.created_at || "").slice(0, 10) },
        { label: "Name", get: (r) => r.name },
        { label: "Company", get: (r) => r.company },
        { label: "Email", get: (r) => r.email },
        { label: "Phone", get: (r) => r.phone },
        { label: "Warehouse size", get: (r) => r.warehouse_size },
        { label: "Sites", get: (r) => r.number_of_sites },
        { label: "Current WMS", get: (r) => r.current_wms },
        { label: "Improvement area", get: (r) => r.improvement_area },
        { label: "Status", get: (r) => r.status || "New" },
        { label: "Message", get: (r) => r.message },
      ];
      downloadCSV(toCSV(filteredDemos, cols), `stockaris-demo-requests-${date}.csv`);
    } else {
      const cols = [
        { label: "Date", get: (r) => (r.created_at || "").slice(0, 10) },
        { label: "Email", get: (r) => r.email },
        { label: "Company", get: (r) => r.company },
        { label: "Overall", get: (r) => r.overall },
        { label: "Inventory", get: (r) => r.scores?.inventory },
        { label: "Inbound", get: (r) => r.scores?.inbound },
        { label: "Outbound", get: (r) => r.scores?.outbound },
        { label: "Pallets", get: (r) => r.scores?.pallets },
        { label: "Planning", get: (r) => r.scores?.planning },
      ];
      downloadCSV(toCSV(filteredChecks, cols), `stockaris-health-checks-${date}.csv`);
    }
  };

  const load = useCallback(async (t) => {
    const tk = t || token;
    if (!tk) return;
    setLoading(true);
    setError("");
    try {
      const headers = { "X-Admin-Token": tk };
      const [d, c, s] = await Promise.all([
        axios.get(`${API}/admin/demo-requests`, { headers }),
        axios.get(`${API}/admin/health-checks`, { headers }),
        axios.get(`${API}/admin/stats`, { headers }),
      ]);
      setDemos(d.data.items);
      setChecks(c.data.items);
      setStats(s.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken("");
        setError("Invalid token. Please try again.");
      } else {
        setError("Could not load admin data.");
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { if (token) load(token); }, []); // eslint-disable-line

  const unlock = (e) => {
    e.preventDefault();
    sessionStorage.setItem(TOKEN_KEY, draft);
    setToken(draft);
    load(draft);
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setDraft("");
    setDemos([]);
    setChecks([]);
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white px-4">
        <form onSubmit={unlock} data-testid="admin-login" className="w-full max-w-sm border border-slate-200 bg-white p-8">
          <Logo />
          <div className="mt-8 flex items-center gap-2 font-mono-p text-xs uppercase tracking-widest text-volt">
            <Lock className="h-4 w-4" /> Internal admin
          </div>
          <p className="mt-2 text-sm text-slate-500">Enter the admin token to view leads.</p>
          <input
            data-testid="admin-token-input"
            type="password"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Admin token"
            className="mt-6 w-full border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-volt"
          />
          <button data-testid="admin-unlock" type="submit" disabled={!draft} className="mt-4 w-full bg-volt py-3 font-display font-bold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-50">
            Unlock
          </button>
          {error && <p data-testid="admin-error" className="mt-4 text-sm text-destructive">{error}</p>}
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pb-20">
      <header className="sticky top-0 z-10 border-b border-slate-200 bg-white/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1200px] items-center justify-between px-6 py-4">
          <Logo />
          <div className="flex items-center gap-3">
            <button data-testid="admin-refresh" onClick={() => load()} className="flex items-center gap-2 border border-slate-200 px-4 py-2 font-mono-p text-xs uppercase tracking-widest text-slate-600 transition-colors hover:border-slate-400">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button data-testid="admin-logout" onClick={logout} className="flex items-center gap-2 border border-slate-200 px-4 py-2 font-mono-p text-xs uppercase tracking-widest text-slate-600 transition-colors hover:border-slate-400">
              <LogOut className="h-4 w-4" /> Lock
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1200px] px-6 pt-10">
        <h1 className="font-display text-4xl font-black">Lead dashboard</h1>
        {error && <p className="mt-3 text-sm text-destructive">{error}</p>}

        <div className="mt-8 grid grid-cols-2 gap-4 sm:max-w-md">
          <StatCard icon={Inbox} label="Demo requests" value={stats.demo_requests} />
          <StatCard icon={Activity} label="Health checks" value={stats.health_checks} />
        </div>

        <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
          <div className="flex gap-2">
            <TabBtn active={tab === "demos"} onClick={() => setTab("demos")} testid="admin-tab-demos">Demo requests ({filteredDemos.length})</TabBtn>
            <TabBtn active={tab === "checks"} onClick={() => setTab("checks")} testid="admin-tab-checks">Health checks ({filteredChecks.length})</TabBtn>
          </div>
          <button
            data-testid="admin-export-csv"
            onClick={exportCurrent}
            className="flex items-center gap-2 bg-volt px-5 py-2.5 font-display text-sm font-bold text-white transition-transform hover:-translate-y-0.5"
          >
            <FileDown className="h-4 w-4" /> Export CSV
          </button>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3 border border-slate-200 bg-white p-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="admin-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search company, name or email…"
              className="w-full border border-slate-200 bg-white py-2.5 pl-9 pr-3 text-sm outline-none focus:border-volt"
            />
          </div>

          <select
            data-testid="admin-range"
            value={range}
            onChange={(e) => setRange(e.target.value)}
            className="border border-slate-200 bg-white px-3 py-2.5 font-mono-p text-xs uppercase tracking-wider text-slate-600 outline-none focus:border-volt"
          >
            {RANGES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>

          {tab === "demos" && (
            <select
              data-testid="admin-area"
              value={area}
              onChange={(e) => setArea(e.target.value)}
              className="border border-slate-200 bg-white px-3 py-2.5 font-mono-p text-xs uppercase tracking-wider text-slate-600 outline-none focus:border-volt"
            >
              <option value="all">All areas</option>
              {IMPROVEMENT_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          )}

          {tab === "demos" && (
            <select
              data-testid="admin-status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-slate-200 bg-white px-3 py-2.5 font-mono-p text-xs uppercase tracking-wider text-slate-600 outline-none focus:border-volt"
            >
              <option value="all">All statuses</option>
              {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          )}

          {hasFilters && (
            <button data-testid="admin-clear-filters" onClick={clearFilters} className="flex items-center gap-1.5 font-mono-p text-xs uppercase tracking-wider text-slate-500 transition-colors hover:text-white">
              <X className="h-4 w-4" /> Clear
            </button>
          )}
        </div>

        {tab === "demos" ? (
          <div className="mt-4 overflow-x-auto border border-slate-200">
            <table className="w-full text-left text-sm" data-testid="admin-demos-table">
              <thead className="border-b border-slate-200 bg-slate-50 font-mono-p text-[10px] uppercase tracking-widest text-slate-400">
                <tr>
                  {["Date", "Name", "Company", "Email", "Phone", "Sites", "Current WMS", "Improve", "Status"].map((h) => (
                    <th key={h} className="px-4 py-3 font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredDemos.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-400">No demo requests match your filters.</td></tr>}
                {filteredDemos.map((d) => (
                  <tr key={d.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono-p text-xs text-slate-500">{(d.created_at || "").slice(0, 10)}</td>
                    <td className="px-4 py-3 font-medium">{d.name}</td>
                    <td className="px-4 py-3">{d.company}</td>
                    <td className="px-4 py-3 text-slate-600">{d.email}</td>
                    <td className="px-4 py-3 text-slate-500">{d.phone || "—"}</td>
                    <td className="px-4 py-3 text-slate-500">{d.number_of_sites || "—"}</td>
                    <td className="px-4 py-3 text-slate-500">{d.current_wms || "—"}</td>
                    <td className="px-4 py-3">{d.improvement_area ? <span className="bg-volt/90 px-2 py-1 font-mono-p text-[10px] font-bold uppercase text-white">{d.improvement_area}</span> : "—"}</td>
                    <td className="px-4 py-3">
                      <select
                        data-testid={`status-select-${d.id}`}
                        value={d.status || "New"}
                        onChange={(e) => updateStatus(d.id, e.target.value)}
                        className={`cursor-pointer rounded border px-2 py-1 font-mono-p text-[10px] font-bold uppercase tracking-wider outline-none ${statusStyle[d.status || "New"]}`}
                      >
                        {LEAD_STATUSES.map((s) => <option key={s} value={s} className="bg-white text-slate-900">{s}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="mt-4 overflow-x-auto border border-slate-200">
            <table className="w-full text-left text-sm" data-testid="admin-checks-table">
              <thead className="border-b border-slate-200 bg-slate-50 font-mono-p text-[10px] uppercase tracking-widest text-slate-400">
                <tr>
                  {["Date", "Email", "Company", "Overall", "Report"].map((h) => (
                    <th key={h} className="px-4 py-3 font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredChecks.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">No health checks match your filters.</td></tr>}
                {filteredChecks.map((c) => (
                  <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono-p text-xs text-slate-500">{(c.created_at || "").slice(0, 10)}</td>
                    <td className="px-4 py-3 text-slate-600">{c.email}</td>
                    <td className="px-4 py-3">{c.company || "—"}</td>
                    <td className={`px-4 py-3 font-display text-lg font-bold ${scoreColor(c.overall)}`}>{c.overall}</td>
                    <td className="px-4 py-3">
                      <a data-testid={`admin-pdf-${c.id}`} href={`${API}/health-check/${c.id}/pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 font-mono-p text-xs uppercase tracking-widest text-volt hover:underline">
                        <Download className="h-3.5 w-3.5" /> PDF
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="border border-slate-200 bg-white p-5">
    <Icon className="h-5 w-5 text-volt" strokeWidth={1.6} />
    <div className="mt-3 font-display text-4xl font-black">{value}</div>
    <div className="font-mono-p text-[10px] uppercase tracking-widest text-slate-400">{label}</div>
  </div>
);

const TabBtn = ({ active, onClick, children, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`px-5 py-2.5 font-mono-p text-xs uppercase tracking-widest transition-colors ${
      active ? "bg-volt text-white" : "border border-slate-200 text-slate-500 hover:border-white/40"
    }`}
  >
    {children}
  </button>
);
